import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CloudWarning, Info, X } from "@phosphor-icons/react";
import { relTime } from "../lib/incidents";

/**
 * Stale-data banner — shown above the incident feed when one or more
 * of the user's county-relevant WildCAD dispatch sources are unreachable.
 *
 * Props:
 *   county: { name, dispatch_centers: [codes...] }
 *   health: from /api/health
 */
export default function StaleDataBanner({ county, health }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed || !county || !health) return null;

  const centers = county.dispatch_centers || [];
  if (centers.length === 0) return null;

  const offline = (health.dispatch_centers || []).filter(
    (d) => centers.includes(d.code) && !d.ok
  );
  if (offline.length === 0) return null;

  const allOffline = offline.length === centers.length;
  const color = allOffline ? "#E63946" : "#FFB347";
  const icon = allOffline ? CloudWarning : Info;
  const Icon = icon;

  return (
    <div
      data-testid="stale-data-banner"
      className="border-b border-tac-border"
      style={{ background: `${color}12` }}
    >
      <div className="px-4 py-2 flex items-start gap-2.5">
        <Icon size={14} weight="fill" style={{ color }} className="shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span
              className="text-[10px] font-bold uppercase tracking-[0.12em]"
              style={{ color }}
            >
              {allOffline ? "Live feed offline" : "Partial feed outage"}
            </span>
            <span className="text-[15px] font-mono-tac text-tac-faint">
              {offline.map((d) => d.code).join(", ")} unreachable
            </span>
          </div>
          <div className="text-[14px] text-tac-dim leading-snug">
            Showing cached {county.name} County data
            {health.last_refresh ? ` — last sync ${relTime(health.last_refresh)}` : ""}.
            {" "}
            <Link
              to="/about"
              data-testid="stale-data-learn-more"
              className="font-bold uppercase tracking-wider text-[10px] text-tac-text hover:text-tac-primary"
            >
              Why?
            </Link>
          </div>
        </div>
        <button
          data-testid="stale-data-dismiss"
          onClick={() => setDismissed(true)}
          className="text-tac-faint hover:text-tac-text p-0.5 shrink-0"
          aria-label="Dismiss"
        >
          <X size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}
